import { useEffect, useRef } from 'react';

import { PAYMENT_METHODS } from '@autopass-web/domain';
import { Loading } from '@autopass-web/mochi-component';
import {
  confirmGuestApsCreditCardTransaction,
  confirmGuestApsEasyWalletTransaction,
  confirmGuestApsJkoPayTransaction,
  confirmGuestApsLinePayTransaction,
  confirmGuestApsTaiwanPayTransaction,
} from '@autopass-web/mochi-payment/payment-models';
import analyticsService from '@autopass-web/mochi-payment/services/analytics';
import { axiosAutoPassErrorConverter } from '@autopass-web/mochi-utils';

import styles from './index.module.scss';

import type { ConfirmPaymentInfo } from '@autopass-web/mochi-payment/types';
import type { AutopassAxiosError } from '@autopass-web/mochi-utils';

type ParsedError = {
  title: string;
  description: string;
  backendErrorCode: string;
  statusCode: number;
  requestUrl: string;
  requestMethod: string;
  requestPayload: Record<string, unknown>;
};

interface ConfirmPaymentModuleProps {
  confirmPaymentInfo: ConfirmPaymentInfo;
  amplitudeKey: string;
  amplitudeEventParams?: Record<string, unknown>;
  onErrorRedirect: (error: Partial<ParsedError>) => void;
  onSuccessRedirect: (result: unknown) => void;
}

/**
 * ## ConfirmPatymentModule
 *
 * currently only support aps. Other projects,
 * like self_service_refueling, chain_store, guest_alpr, etc,
 * should implement their own features before integrating this module.
 *
 * 2024/04/03
 */

export const ConfirmPaymentModule = ({
  confirmPaymentInfo,
  amplitudeKey,
  amplitudeEventParams,
  onErrorRedirect,
  onSuccessRedirect,
}: ConfirmPaymentModuleProps) => {
  const { paymentType, message, result, title, status } = confirmPaymentInfo;
  const hasCheckedRef = useRef<boolean>(false);

  useEffect(() => {
    analyticsService.init(amplitudeKey, amplitudeEventParams);
  }, [amplitudeKey, amplitudeEventParams]);

  useEffect(() => {
    if (hasCheckedRef.current === true) return;

    async function checkIfPaymentSuccess() {
      hasCheckedRef.current = true;

      // OTHER PAYMENT TYPES
      if (result === 'fail') {
        onErrorRedirect({ title, description: message });
        return;
      }

      // LINE PAY ONLY
      if (status === 'failed') {
        onErrorRedirect({ title, description: message });
        return;
      }

      let data: unknown;

      try {
        switch (paymentType) {
          case PAYMENT_METHODS.CREDIT_CARD: {
            data = await confirmGuestApsCreditCardTransaction({
              confirmPaymentInfo,
            });
            break;
          }
          case PAYMENT_METHODS.LINE_PAY: {
            data = await confirmGuestApsLinePayTransaction({
              confirmPaymentInfo,
            });
            break;
          }
          case PAYMENT_METHODS.JKOPAY: {
            data = await confirmGuestApsJkoPayTransaction({
              confirmPaymentInfo,
            });
            break;
          }
          case PAYMENT_METHODS.EASY_WALLET: {
            data = await confirmGuestApsEasyWalletTransaction({
              confirmPaymentInfo,
            });
            break;
          }
          case PAYMENT_METHODS.TAIWAN_PAY: {
            data = await confirmGuestApsTaiwanPayTransaction({
              confirmPaymentInfo,
            });
            break;
          }
          default:
            throw new Error('Invalid payment type');
        }

        onSuccessRedirect(data);
      } catch (error) {
        const result = axiosAutoPassErrorConverter(error as AutopassAxiosError);

        if (result == null) return;

        onErrorRedirect({
          title: result.message,
          backendErrorCode: result.code,
          statusCode: result.metadata?.status,
          requestUrl: result.metadata?.url,
          requestMethod: result.metadata?.method,
          requestPayload: result.metadata?.payload ?? undefined,
        });
      }
    }

    checkIfPaymentSuccess();
  }, [
    paymentType,
    title,
    message,
    onErrorRedirect,
    onSuccessRedirect,
    status,
    result,
    confirmPaymentInfo,
  ]);

  return (
    <div className={styles['confirm-payment']}>
      <Loading inline />
      <div>交易處理中，請稍候</div>
      <div
        style={{
          fontSize: 14,
          marginTop: 6,
          width: '70%',
        }}
      >
        為了確保交易成功，請勿重新整理或關閉頁面。
      </div>
    </div>
  );
};
